import React, { useState, useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useForm, Controller } from "react-hook-form";
import * as Sentry from "@sentry/react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Container,
  Main,
  Form,
  Fields,
  Divider,
  Cart,
  CartWrapper,
  CartToggler,
  SubmitContainer,
  BackToCart,
  SubmitButtonContainer,
  ShippingContainer,
  ShippingCost,
  AddressValidationBanner,
  AddressValidationTitle,
  AddressValidationBody,
  AddressValidationSuggestion,
  SuggestionAddress,
} from "./CheckoutView.style";

import { Layout } from "../../components/Layout/Layout";
import { Header } from "../../components/Header/Header";
import { Breadcrumbs } from "../../components/Breadcrumbs/Breadcrumbs";
import { PageTitle } from "../../components/PageTitle/PageTitle";
import { Footer } from "../../components/Footer/Footer";
import { Loading } from "../../components/Loading/Loading";

import { Typography } from "../../ui/Typography/Typography";
import { TextInput } from "../../ui/TextInput/TextInput";
import { Select } from "../../ui/Select/Select";
import { Radio } from "../../ui/Radio/Radio";
import { Checkbox } from "../../ui/Checkbox/Checkbox";
import { Button } from "../../ui/Button/Button";
import { CheckoutCart } from "../../components/CheckoutCart/CheckoutCart";
import { Link } from "../../components/Link/Link";
// import { brands } from "../../config/brands";
import {
  toast,
  ToastNotification,
} from "../../ui/ToastNotification/ToastNotification";
import { GET_CART_DETAILS, CHECKOUT } from "../RewardsView/rewardsViewConfig";
import { shippingOptions, provinces } from "../../config/checkout";
import { apiFetch } from "../../utils/api-fetch";
import {
  CA_POSTAL_REGEX,
  normalizeCaPostal,
  POSTAL_FORMAT_MESSAGE,
} from "../../utils/postal";
import { apiSiteId } from "../../config";

const address = yup.object({
  str1: yup.string().required().label("address line 1").default(""),
  str2: yup.string().label("address line 2").default(""),
  city: yup.string().required().label("city").default(""),
  postal: yup
    .string()
    .required()
    .transform((v) => normalizeCaPostal(v))
    .matches(CA_POSTAL_REGEX, POSTAL_FORMAT_MESSAGE)
    .label("postal code")
    .default(""),
  country: yup.string().required().label("country").default("CA"),
  state: yup.string().required().label("province").default(""),
  data: yup.object({
    firstname: yup.string().required().label("first name").default(""),
    lastname: yup.string().required().label("last name").default(""),
    phone: yup.string().required().label("phone").default(""),
    addressType: yup.string().required().label("address type").default(""),
  }),
});

const shipping = yup.object({
  method: yup
    .string()
    .required()
    .label("shipping method")
    .default(shippingOptions.find((o) => o.default).id),
  address,
});

const billing = yup.object({
  method: yup.string().required().label("billing method").default("points"),
  // data: yup.object({
  //   card: yup.string().label("card number").default(null),
  //   cvv2: yup.string().label("cvv2").default(null),
  //   expiresYear: yup.string().label("expires year").default(null),
  //   expiresMonth: yup.string().label("expires month").default(null),
  // }),
  // address, // this will be passed in right before submitting
});

const schema = yup.object({
  shipping,
  billing,
  terms: yup
    .boolean()
    .oneOf([true], "Field must be checked")
    .required()
    .label("terms and conditions")
    .default(false),
});

export const CheckoutView = () => {
  const intl = useIntl();
  const [cartIsShow, setCartIsShow] = useState(false);
  const checkoutComplete = React.useRef(false);
  const [addrValidation, setAddrValidation] = useState({
    status: "idle", // 'idle' | 'pending' | 'valid' | 'invalid'
    suggestion: null,
    accepted: false,
  });
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    trigger,
    formState: { errors, isSubmitting, isValid },
  } = useForm({
    mode: "onTouched",
    resolver: yupResolver(schema),
    defaultValues: schema.default(),
  });
  const {
    loading: cartLoading,
    error: cartError,
    data: cartData,
  } = useQuery(GET_CART_DETAILS, {
    variables: {
      locale: intl.locale,
    },
  });

  const cart = cartData && cartData.me && cartData.me.cart;
  const pointsBalance = cartData && cartData.me && cartData.me.pointsBalance;
  const cartItems = (cart && cart.items) || [];

  const navigate = useNavigate();

  const [checkout] = useMutation(CHECKOUT, {
    refetchQueries: ["GetHeaderCartData", "GetCartDetails"],
  });

  const data = watch();

  useEffect(() => {
    const user = cartData && cartData.me && cartData.me.user;
    if (user && user._id) {
      const salon = (user.salon) || {};
      const salonAddress = salon.address || {};
      const salonAddressData = salonAddress.data || {};
      setValue("shipping.address.data.firstname", user.firstname || "");
      setValue("shipping.address.data.lastname", user.lastname || "");
      setValue("shipping.address.data.phone", user.phone || "");
      setValue("shipping.address.str1", salonAddress.str1 || "");
      setValue("shipping.address.str2", salonAddress.str2 || "");
      setValue("shipping.address.city", salonAddress.city || "");
      setValue("shipping.address.state", salonAddress.state || "");
      setValue("shipping.address.postal", normalizeCaPostal(salonAddress.postal || ""));
      setValue("shipping.address.data.addressType", salonAddressData.addressType || "");
    }
  }, [cartData && cartData.me && cartData.me.user && cartData.me.user._id]);

  useEffect(() => {
      if (cartLoading || cartError) {
          return;
      }
    if (checkoutComplete.current) return;

    const cartTotalAmount = cart && cart.totals && cart.totals.pointsTotal && cart.totals.pointsTotal.amount;
    const userId = cartData && cartData.me && cartData.me.user && cartData.me.user._id;

    if (
      cartItems.length === 0 ||
      pointsBalance < cartTotalAmount ||
      userId == null
    ) {
      navigate(`/${intl.locale}/cart`, { replace: true });
      return;
    }

    // Check if education reward exists and has attendee data
    const educationItem = cartItems.find(
      function(item) { return item.product && item.product.type === "education-reward"; }
    );

    if (educationItem) {
      const attendees = educationItem.data && educationItem.data.attendees;
      const hasAttendees = attendees && attendees.length === educationItem.qty;

      if (!hasAttendees) {
        navigate(`/${intl.locale}/attendees/${educationItem.product._id}`, {
          replace: true,
        });
        return;
      }
    }
  }, [cartLoading, cartError, cartItems, pointsBalance, cart, cartData, navigate, intl.locale]);

  if (cartLoading) {
    return <Loading global />;
  }

  const resetAddressValidation = () => {
    setAddrValidation({ status: "idle", suggestion: null, accepted: false });
  };

  const triggerAddressVerification = async () => {
    const addr = getValues("shipping.address");
    const { str1, city, state } = addr;
    const postal = normalizeCaPostal(addr.postal);
    if (!str1 || !city || !state || !postal) return;

    // Never hit LOB with a malformed Canadian postal code. The yup
    // schema will surface the format error to the user.
    if (!CA_POSTAL_REGEX.test(postal)) {
      setAddrValidation({ status: "idle", suggestion: null, accepted: false });
      return;
    }

    // Reflect the normalized value in the form so what the user sees
    // matches what we send to the server.
    if (postal !== addr.postal) {
      setValue("shipping.address.postal", postal, { shouldValidate: true });
    }

    setAddrValidation({ status: "pending", suggestion: null, accepted: false });
    try {
      const { result } = await apiFetch({
        path: `/${apiSiteId}/address/verify`,
        data: { str1, str2: addr.str2 || "", city, state, postal, country: "CA" },
      });
      if (result.valid) {
        setAddrValidation({ status: "valid", suggestion: null, accepted: false });
      } else {
        // Only surface the suggestion when LOB considers the address deliverable.
        // "undeliverable" means the address doesn't exist — no suggestion is helpful.
        const deliverable = result.deliverability !== "undeliverable";
        setAddrValidation({
          status: "invalid",
          suggestion: deliverable ? (result.suggestion || null) : null,
          accepted: false,
        });
      }
    } catch (err) {
      // If Lob is unavailable, allow checkout to proceed rather than blocking the user.
      setAddrValidation({ status: "idle", suggestion: null, accepted: false });
      console.error({ err, type: "addressVerification" });
    }
  };

  const handleAddressTypeChange = (fieldOnChange, value) => {
    fieldOnChange(value);
    triggerAddressVerification();
  };

  const canSubmit =
    addrValidation.status === "valid" ||
    addrValidation.status === "idle" ||
    (addrValidation.status === "invalid" && addrValidation.accepted);

  const onSubmit = (data) => {
    const payload = { ...data };
    payload.billing.address = { ...payload.shipping.address };
    return checkout({
      variables: { data: payload, locale: intl.locale },
    })
      .then((results) => {
        checkoutComplete.current = true;
        navigate(`/${intl.locale}/order-confirmation`, {
          replace: true,
          state: { orderId: results && results.data && results.data.cartPlaceOrder && results.data.cartPlaceOrder.fid },
        });
      })
      .catch((err) => {
        toast.error(
          <ToastNotification>
            <FormattedMessage defaultMessage="An error occurred while checking out.  Please refresh and try again." />
          </ToastNotification>
        );
        Sentry.captureException(err);
        console.error({ err, type: "checkout" });
      });
  };

  return (
    <Layout>
      <Header />
      <Container>
        <Main>
          <Breadcrumbs
            breadcrumbs={[
              {
                to: "/",
                label: intl.formatMessage({ defaultMessage: "Home" }),
              },
              {
                to: null,
                label: intl.formatMessage({ defaultMessage: "Checkout" }),
              },
            ]}
          />
          <PageTitle
            title={intl.formatMessage({ defaultMessage: "Checkout" })}
          />

          <Cart className="--mobile">
            <CartWrapper>
              <CartToggler
                type="button"
                $isActive={cartIsShow}
                onClick={() => setCartIsShow(!cartIsShow)}
              >
                <Typography type="text-small" color="#000">
                  <FormattedMessage defaultMessage="Total: 18,000 PTS" />
                </Typography>

                <span style={{ display: "flex", alignItems: "center" }}>
                  <Typography type="text-small">
                    {cartIsShow ? (
                      <FormattedMessage defaultMessage="Hide Cart" />
                    ) : (
                      <FormattedMessage defaultMessage="Show Cart" />
                    )}
                  </Typography>
                  &nbsp;&nbsp;
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 9 9"
                    width={9}
                    height={9}
                  >
                    <path
                      fill="currentColor"
                      d={
                        cartIsShow
                          ? "M.35 5.15v-1.3h8.3v1.3H.35Z"
                          : "M3.808 8.748V5.474l-.282-.287H.251V3.798h3.24l.314-.311V.248h1.39v3.243l.31.312H8.75v1.388H5.478l-.282.287v3.274l-1.388-.004Z"
                      }
                    />
                  </svg>
                </span>
              </CartToggler>
              {cartIsShow && <CheckoutCart cart={cart} />}
            </CartWrapper>
          </Cart>

          <Form onSubmit={handleSubmit(onSubmit)}>
            <Typography type="h3" style={{ marginBottom: 20 }}>
              <FormattedMessage defaultMessage="Shipping Details" />
            </Typography>

            <Fields>
              <Controller
                name="shipping.address.data.firstname"
                control={control}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    label={intl.formatMessage({ defaultMessage: "First Name" })}
                    error={errors && errors.shipping && errors.shipping.address && errors.shipping.address.data && errors.shipping.address.data.firstname}
                  />
                )}
              />
              <Controller
                name="shipping.address.data.lastname"
                control={control}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    label={intl.formatMessage({ defaultMessage: "Last Name" })}
                    error={errors && errors.shipping && errors.shipping.address && errors.shipping.address.data && errors.shipping.address.data.lastname}
                  />
                )}
              />
              <Controller
                name="shipping.address.str1"
                control={control}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    onChange={(e) => { field.onChange(e); resetAddressValidation(); }}
                    onBlur={triggerAddressVerification}
                    label={intl.formatMessage({
                      defaultMessage: "Address 1",
                    })}
                    error={errors && errors.shipping && errors.shipping.address && errors.shipping.address.str1}
                  />
                )}
              />
              <Controller
                name="shipping.address.str2"
                control={control}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    onChange={(e) => { field.onChange(e); resetAddressValidation(); }}
                    onBlur={triggerAddressVerification}
                    label={intl.formatMessage({
                      defaultMessage: "Address 2 (Optional)",
                    })}
                    error={errors && errors.shipping && errors.shipping.address && errors.shipping.address.str2}
                  />
                )}
              />
              <Controller
                name="shipping.address.state"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    onChange={(value) => { field.onChange(value); resetAddressValidation(); triggerAddressVerification(); }}
                    control={control}
                    label={intl.formatMessage({ defaultMessage: "Province" })}
                    options={provinces}
                  />
                )}
                error={errors && errors.shipping && errors.shipping.address && errors.shipping.address.state}
              />
              <Controller
                name="shipping.address.city"
                control={control}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    onChange={(e) => { field.onChange(e); resetAddressValidation(); }}
                    onBlur={triggerAddressVerification}
                    label={intl.formatMessage({
                      defaultMessage: "City",
                    })}
                    error={errors && errors.shipping && errors.shipping.address && errors.shipping.address.city}
                  />
                )}
              />
              <Controller
                name="shipping.address.postal"
                control={control}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    maxLength={7}
                    onChange={(e) => {
                      const raw = e && e.target ? e.target.value : e;
                      const formatted = normalizeCaPostal(raw);
                      field.onChange(formatted);
                      resetAddressValidation();
                      trigger("shipping.address.postal");
                    }}
                    onBlur={() => {
                      field.onChange(normalizeCaPostal(field.value));
                      trigger("shipping.address.postal");
                      triggerAddressVerification();
                    }}
                    label={intl.formatMessage({
                      defaultMessage: "Postal Code",
                    })}
                    error={errors && errors.shipping && errors.shipping.address && errors.shipping.address.postal}
                  />
                )}
              />
              <Controller
                name="shipping.address.data.addressType"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    onChange={(value) => handleAddressTypeChange(field.onChange, value)}
                    control={control}
                    label={intl.formatMessage({
                      defaultMessage: "Address Type",
                    })}
                    options={[
                      {
                        value: "Business",
                        label: intl.formatMessage({
                          defaultMessage: "Business",
                        }),
                      },
                      {
                        value: "Residential",
                        label: intl.formatMessage({
                          defaultMessage: "Residential",
                        }),
                      },
                    ]}
                    style={{ paddingBottom: 0 }}
                    error={errors && errors.shipping && errors.shipping.address && errors.shipping.address.data && errors.shipping.address.data.addressType}
                  />
                )}
              />
              {addrValidation.status === "pending" && (
                <AddressValidationBanner>
                  <AddressValidationTitle>
                    <FormattedMessage
                      id="checkout.address.validation.loading"
                      defaultMessage="Verifying address…"
                    />
                  </AddressValidationTitle>
                </AddressValidationBanner>
              )}
              {addrValidation.status === "invalid" && (
                <AddressValidationBanner>
                  <AddressValidationTitle>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      width={16}
                      height={16}
                      fill="#e85c41"
                      style={{ flexShrink: 0, marginTop: 1 }}
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0zm-8-5a.75.75 0 0 1 .75.75v4.5a.75.75 0 0 1-1.5 0v-4.5A.75.75 0 0 1 10 5zm0 10a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <FormattedMessage
                      id="checkout.address.validation.error.title"
                      defaultMessage="Oops! We couldn't recognize your selected address."
                    />
                  </AddressValidationTitle>
                  {addrValidation.suggestion ? (
                    <>
                      <AddressValidationBody>
                        <FormattedMessage
                          id="checkout.address.validation.error.body"
                          defaultMessage="We're having trouble finding your address. Please make sure all fields are filled out correctly. You can also select a suggested address below if it looks right to you to proceed."
                        />
                      </AddressValidationBody>
                      <AddressValidationSuggestion>
                        <input
                          type="checkbox"
                          checked={addrValidation.accepted}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setAddrValidation((prev) => ({ ...prev, accepted: checked }));
                            if (checked && addrValidation.suggestion) {
                              const s = addrValidation.suggestion;
                              setValue("shipping.address.str1", s.str1 || "");
                              if (s.str2 !== undefined) setValue("shipping.address.str2", s.str2 || "");
                              setValue("shipping.address.city", s.city || "");
                              setValue("shipping.address.state", s.state || "");
                              setValue("shipping.address.postal", normalizeCaPostal(s.postal || ""));
                            }
                          }}
                        />
                        <span>
                          <FormattedMessage
                            id="checkout.address.validation.suggestion.label"
                            defaultMessage="Continue with this address:"
                          />
                          {" "}
                          <SuggestionAddress>
                            {[
                              addrValidation.suggestion.str1,
                              addrValidation.suggestion.city,
                              addrValidation.suggestion.state,
                              addrValidation.suggestion.postal,
                            ]
                              .filter(Boolean)
                              .join(", ")}
                          </SuggestionAddress>
                        </span>
                      </AddressValidationSuggestion>
                    </>
                  ) : (
                    <AddressValidationBody>
                      <FormattedMessage
                        id="checkout.address.validation.undeliverable.body"
                        defaultMessage="We're having trouble finding your address. Please make sure all fields are filled out correctly. If you are still having issues, please reach out to our <supportLink>Support Team</supportLink> with your address details for further assistance."
                        values={{
                          supportLink: (chunks) => (
                            <a
                              href="https://clubhrewards.zendesk.com/hc/en-us/requests/new"
                              target="_blank"
                              rel="noreferrer"
                              style={{ textDecoration: "underline" }}
                            >
                              {chunks}
                            </a>
                          ),
                        }}
                      />
                    </AddressValidationBody>
                  )}
                </AddressValidationBanner>
              )}
              <Controller
                name="shipping.address.data.phone"
                control={control}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    label={intl.formatMessage({ defaultMessage: "Phone" })}
                    error={errors && errors.shipping && errors.shipping.address && errors.shipping.address.data && errors.shipping.address.data.phone}
                  />
                )}
              />
            </Fields>

            <Divider style={{ marginTop: 26, marginBottom: 41 }} />

            <Typography type="h3" style={{ marginBottom: 20 }}>
              <FormattedMessage defaultMessage="Shipping Method" />
            </Typography>

            <ShippingContainer>
              <Controller
                name="shipping.method"
                control={control}
                render={({ field }) =>
                  shippingOptions.map((option) => {
                    return (
                      <Radio
                        key={option.id}
                        {...field}
                        checked={field.value === option.id ? true : false}
                        onChange={(e) => {
                          // console.info(JSON.stringify(e.target.checked));
                          field.onChange(e.target.checked ? option.id : "");
                        }}
                        label={option.description(intl)}
                        style={{ maxWidth: 330 }}
                      />
                    );
                  })
                }
              />
              <ShippingCost>
                <Typography type="h4">$0.00</Typography>
              </ShippingCost>
            </ShippingContainer>

            <Divider />

            <Typography type="h3" style={{ marginBottom: 13 }}>
              <FormattedMessage defaultMessage="Payment Method" />
            </Typography>
            <Typography type="text-primary">
              <FormattedMessage defaultMessage="No payment method required." />
            </Typography>

            <Divider />

            <Controller
              name="terms"
              control={control}
              render={({ field }) => (
                <Checkbox
                  {...field}
                  label={intl.formatMessage(
                    {
                      defaultMessage:
                        "By clicking 'Place Order', I agree to be bound by the {termsOfUse}, and I acknowledge and consent to the practices described in Henkel’s {privacyPolicy}.",
                    },
                    {
                      termsOfUse: (
                        <a
                          href={intl.formatMessage({
                            defaultMessage:
                              "https://www.henkel-northamerica.com/terms-of-use-na",
                          })}
                          target="_blank"
                          style={{ color: "#000" }}
                        >
                          {intl.formatMessage({
                            defaultMessage: "Henkel Terms of Use",
                          })}
                        </a>
                      ),
                      privacyPolicy: (
                        <a
                          href={intl.formatMessage({
                            defaultMessage:
                              "https://www.henkel-northamerica.com/privacy-statement-na",
                          })}
                          target="_blank"
                          style={{ color: "#000" }}
                        >
                          {intl.formatMessage({
                            defaultMessage: "Privacy Policy",
                          })}
                        </a>
                      ),
                    }
                  )}
                  error={errors && errors.terms}
                />
              )}
            />

            <SubmitContainer>
              <BackToCart as={Link} to="/cart">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 14 14"
                  width={14}
                  height={14}
                >
                  <path
                    fill="#8f95a5"
                    d="m10.305 1.708-5.11 5.1v.389l5.11 5.1-1.06 1.055-5.549-5.539V6.192L9.245.649l1.06 1.059Z"
                  />
                </svg>
                <Typography type="text-small">
                  <FormattedMessage defaultMessage="Back to Cart" />
                </Typography>
              </BackToCart>

              <SubmitButtonContainer>
                <Button
                  variant="auth"
                  arrow={false}
                  disabled={
                    isSubmitting ||
                    !canSubmit ||
                    !!(errors && errors.shipping && errors.shipping.address && errors.shipping.address.postal)
                  }
                >
                  <FormattedMessage defaultMessage="Place order" />
                </Button>
              </SubmitButtonContainer>
            </SubmitContainer>
          </Form>
        </Main>

        <Cart className="--desktop">
          <CheckoutCart cart={cart} />
        </Cart>
      </Container>
      <Footer />
    </Layout>
  );
};
